import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { useGroup } from '../lib/GroupContext'
import { track, Events } from '../lib/analytics'
import Logo from '../components/Logo'
import Page from '../components/Page'
import TabBar from '../components/TabBar'

export default function GroupSettings() {
  const { user, profile, fetchProfile, signOut } = useAuth()
  const { groups, activeGroup, switchGroup, refreshGroups } = useGroup()
  const navigate = useNavigate()
  const [group, setGroup] = useState(null)
  const [members, setMembers] = useState([])
  const [isHost, setIsHost] = useState(false)
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(true)
  const [editingName, setEditingName] = useState(false)
  const [newName, setNewName] = useState('')
  const [savingName, setSavingName] = useState(false)
  const [leavingGroupId, setLeavingGroupId] = useState(null)
  const [showWelcome, setShowWelcome] = useState(() => {
    try {
      return localStorage.getItem('flickpick_skip_gate') !== 'true'
    } catch {
      return true
    }
  })

  function toggleWelcome(next) {
    setShowWelcome(next)
    try {
      localStorage.setItem('flickpick_skip_gate', next ? 'false' : 'true')
    } catch { /* ignore */ }
  }

  useEffect(() => {
    loadGroup()
  }, [user, activeGroup])

  async function loadGroup() {
    if (!activeGroup) { setLoading(false); return }

    const { data: membership } = await supabase
      .from('group_members')
      .select('*, groups(*)')
      .eq('user_id', user.id)
      .eq('group_id', activeGroup.id)
      .single()

    if (!membership) { setLoading(false); return }

    setGroup(membership.groups)
    setIsHost(membership.role === 'host')

    // Get all members with suggestion counts
    const { data: mems } = await supabase
      .from('group_members')
      .select('*, profiles(display_name)')
      .eq('group_id', activeGroup.id)
      .order('joined_at', { ascending: true })

    // Get suggestion counts per user
    const { data: suggestions } = await supabase
      .from('suggestions')
      .select('user_id')
      .eq('group_id', membership.groups.id)

    const counts = {}
    suggestions?.forEach(s => {
      counts[s.user_id] = (counts[s.user_id] || 0) + 1
    })

    setMembers((mems || []).map(m => ({
      ...m,
      suggestionCount: counts[m.user_id] || 0,
    })))

    setLoading(false)
  }

  function getInviteUrl() {
    return `${window.location.origin}/join/${group?.invite_code}`
  }

  async function copyInviteLink() {
    if (!group) return
    try {
      await navigator.clipboard.writeText(getInviteUrl())
      track(Events.LinkShared, { surface: 'group_invite', method: 'copy' })
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      track(Events.LinkShared, { surface: 'group_invite', method: 'prompt_fallback' })
      prompt('Copy this invite link:', getInviteUrl())
    }
  }

  async function shareInviteLink() {
    if (!group) return
    try {
      await navigator.share({
        title: `Join ${group.name} on FlickPick`,
        text: `You've been invited to join "${group.name}" on FlickPick — the family movie night picker!`,
        url: getInviteUrl(),
      })
      track(Events.LinkShared, { surface: 'group_invite', method: 'native' })
    } catch (err) {
      // User cancelled or share not supported — fall back to copy
      if (err.name !== 'AbortError') copyInviteLink()
    }
  }

  async function regenerateInvite() {
    if (!isHost || !group) return
    const newCode = crypto.randomUUID().split('-')[0].toUpperCase()
    await supabase.from('groups').update({ invite_code: newCode }).eq('id', group.id)
    setGroup({ ...group, invite_code: newCode })
  }

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center">
        <span className="text-[var(--fp-text-muted)]">Loading...</span>
      </div>
    )
  }

  return (
    <Page mode="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between py-4 mb-10">
        <Logo size="md" />
        <button
          onClick={() => navigate('/')}
          className="text-sm font-semibold text-[var(--fp-red)] bg-transparent border-none cursor-pointer"
        >
          Done
        </button>
      </div>

      <h1 className="fp-display text-3xl text-white mb-14">
        {group?.name || 'Group'}
      </h1>

      {/* Your Profile */}
      <div className="fp-card-elevated mb-14">
        <div className="text-[11px] fp-gold-text uppercase tracking-[0.25em] font-semibold mb-5">
          Your Profile
        </div>
        {editingName ? (
          <div className="flex gap-3">
            <input
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              className="fp-input flex-1"
              placeholder="Display name"
              autoFocus
            />
            <button
              onClick={async () => {
                if (!newName.trim()) return
                setSavingName(true)
                await supabase.from('profiles').update({ display_name: newName.trim() }).eq('id', user.id)
                await fetchProfile(user.id)
                await loadGroup()
                setSavingName(false)
                setEditingName(false)
              }}
              disabled={savingName}
              className="fp-btn fp-btn-primary"
              style={{ width: 'auto', padding: '14px 20px' }}
            >
              {savingName ? '...' : 'Save'}
            </button>
            <button
              onClick={() => setEditingName(false)}
              className="text-sm text-[var(--fp-text-muted)] bg-transparent border-none cursor-pointer"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <span className="text-lg font-semibold text-white">{profile?.display_name || 'Unknown'}</span>
              <p className="text-sm text-[var(--fp-text-muted)] mt-2">{user?.email}</p>
            </div>
            <button
              onClick={() => { setNewName(profile?.display_name || ''); setEditingName(true) }}
              className="text-sm font-semibold text-[var(--fp-red)] bg-transparent border-none cursor-pointer"
            >
              Edit name
            </button>
          </div>
        )}
      </div>

      {/* Members */}
      <div className="mb-14">
        <div className="text-[11px] fp-gold-text uppercase tracking-[0.25em] font-semibold mb-5 pl-2">
          Members ({members.length})
        </div>
        <div className="fp-card">
          {members.map((m, i) => (
            <div
              key={m.id}
              className="flex items-center gap-5 py-5 border-b border-[var(--fp-border)] last:border-b-0 first:pt-0 last:pb-0"
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-base font-bold text-white flex-shrink-0"
                style={{ background: i % 2 === 0 ? 'var(--fp-red)' : 'var(--fp-gold)', color: i % 2 === 0 ? '#fff' : '#0a0a0a' }}
              >
                {m.profiles?.display_name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="flex-1">
                <span className="text-base font-semibold text-white">
                  {m.profiles?.display_name || 'Unknown'}
                </span>
                {m.role === 'host' && (
                  <span
                    className="ml-3 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                    style={{ background: 'var(--fp-gold-soft)', color: 'var(--fp-gold)' }}
                  >
                    Host
                  </span>
                )}
              </div>
              <span className="text-sm text-[var(--fp-text-muted)]">
                {m.suggestionCount} pick{m.suggestionCount !== 1 ? 's' : ''}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Invite */}
      <div className="mb-14">
        <div className="text-[11px] fp-gold-text uppercase tracking-[0.25em] font-semibold mb-5 pl-2">
          Invite Link
        </div>
        <div className="fp-card text-center" style={{ paddingTop: '40px', paddingBottom: '40px' }}>
          <p className="text-sm text-[var(--fp-text-muted)] mb-4">Share this link to invite people to <span className="text-white font-semibold">{group?.name}</span></p>
          <p className="text-sm text-[var(--fp-text-muted)] mb-1">flickpick.com.au/join/</p>
          <p className="text-2xl font-bold text-white font-mono tracking-widest mb-6">
            {group?.invite_code}
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            {typeof navigator.share === 'function' && (
              <button
                onClick={shareInviteLink}
                className="fp-btn fp-btn-primary"
                style={{ width: 'auto', padding: '16px 36px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                  <polyline points="16 6 12 2 8 6"/>
                  <line x1="12" y1="2" x2="12" y2="15"/>
                </svg>
                Share Invite
              </button>
            )}
            <button
              onClick={copyInviteLink}
              className={`fp-btn ${typeof navigator.share === 'function' ? 'fp-btn-secondary' : 'fp-btn-primary'}`}
              style={{ width: 'auto', padding: '16px 36px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
              {copied ? '✓ Copied!' : 'Copy Link'}
            </button>
          </div>
          {isHost && (
            <button
              onClick={regenerateInvite}
              className="block mx-auto mt-5 text-xs text-[var(--fp-text-dim)] bg-transparent border-none cursor-pointer hover:text-[var(--fp-red)]"
            >
              Regenerate invite code
            </button>
          )}
        </div>
      </div>

      {/* Your Groups */}
      <div className="mb-14">
        <div className="text-[11px] fp-gold-text uppercase tracking-[0.25em] font-semibold mb-5 pl-2">
          Your Groups ({groups.length})
        </div>
        <div className="fp-card">
          {groups.map((g, i) => {
            const isActive = g.id === activeGroup?.id
            return (
              <div
                key={g.id}
                className="flex items-center gap-4 py-5 border-b border-[var(--fp-border)] last:border-b-0 first:pt-0 last:pb-0"
              >
                <button
                  onClick={() => switchGroup(g.id)}
                  className="flex-1 text-left bg-transparent border-none cursor-pointer p-0"
                >
                  <span className={`text-base font-semibold ${isActive ? 'text-white' : 'text-[var(--fp-text-muted)]'}`}>
                    {g.name}
                  </span>
                  {isActive && (
                    <span
                      className="ml-3 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full"
                      style={{ background: 'var(--fp-gold-soft)', color: 'var(--fp-gold)' }}
                    >
                      Active
                    </span>
                  )}
                </button>
                <button
                  onClick={async () => {
                    if (!confirm(`Leave "${g.name}"? You'll need a new invite to rejoin.`)) return
                    setLeavingGroupId(g.id)
                    const { error } = await supabase
                      .from('group_members')
                      .delete()
                      .eq('user_id', user.id)
                      .eq('group_id', g.id)
                    if (error) {
                      alert('Failed to leave group. Please try again.')
                      setLeavingGroupId(null)
                      return
                    }
                    await refreshGroups()
                    setLeavingGroupId(null)
                  }}
                  disabled={leavingGroupId === g.id}
                  className="text-xs font-semibold bg-transparent border-none cursor-pointer px-3 py-2 rounded-lg hover:bg-[rgba(200,16,46,0.1)] transition-colors"
                  style={{ color: 'var(--fp-red)' }}
                >
                  {leavingGroupId === g.id ? '...' : 'Leave'}
                </button>
              </div>
            )
          })}
        </div>
        <div className="flex gap-3 mt-5">
          <button
            onClick={() => navigate('/create-group')}
            className="fp-btn fp-btn-secondary flex-1"
            style={{ padding: '14px' }}
          >
            Create new group
          </button>
        </div>
      </div>

      {/* Preferences */}
      <div className="mb-14">
        <div className="text-[11px] fp-gold-text uppercase tracking-[0.25em] font-semibold mb-5 pl-2">
          Preferences
        </div>
        <div className="fp-card">
          <label className="flex items-center justify-between gap-4 cursor-pointer select-none">
            <div>
              <div className="text-base font-semibold text-white">Show welcome screen on login</div>
              <p className="text-sm text-[var(--fp-text-muted)] mt-1">
                Pick "Just me" or "My crew" each time you sign in.
              </p>
            </div>
            <input
              type="checkbox"
              checked={showWelcome}
              onChange={e => toggleWelcome(e.target.checked)}
              className="w-6 h-6 cursor-pointer flex-shrink-0"
              style={{ accentColor: 'var(--fp-red)' }}
            />
          </label>
        </div>
      </div>

      {/* Sign Out */}
      <button
        onClick={async () => { await signOut(); navigate('/login') }}
        className="fp-btn fp-btn-secondary"
      >
        Sign out
      </button>

      <TabBar />
    </Page>
  )
}
